import { useState } from "react";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { User, Mail, Shield, Server, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function SettingsPage() {
  const { user, token } = useAuth();
  const [passwords, setPasswords] = useState({ current: "", next: "", confirm: "" });
  const [showPasswords, setShowPasswords] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const updatePasswordField = (event) => {
    setPasswords((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const changePassword = async (event) => {
    event.preventDefault();
    if (passwords.next.length < 12) {
      toast.error("Le nouveau mot de passe doit contenir au moins 12 caractères.");
      return;
    }
    if (passwords.next !== passwords.confirm) {
      toast.error("La confirmation ne correspond pas au nouveau mot de passe.");
      return;
    }

    setSavingPassword(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: passwords.current,
          new_password: passwords.next,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Modification impossible.");
      setPasswords({ current: "", next: "", confirm: "" });
      toast.success(data.message || "Mot de passe modifié avec succès.");
    } catch (error) {
      toast.error(error.message || "Erreur de connexion au serveur.");
    } finally {
      setSavingPassword(false);
    }
  };

  const row = (Icon, label, value) => (
    <div className="flex items-center gap-3 py-3 border-b border-slate-100 last:border-0">
      <Icon className="w-4 h-4 text-slate-400" />
      <div className="text-sm text-slate-500 w-36">{label}</div>
      <div className="text-sm font-semibold text-slate-900">{value || "—"}</div>
    </div>
  );

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8 max-w-2xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Paramètres</h1>
          <p className="text-slate-500 text-sm mt-0.5">Votre compte et l'application</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 mb-4">
          <h2 className="font-bold text-slate-900 mb-2">Mon compte</h2>
          {row(User, "Nom", user?.name)}
          {row(Mail, "Email", user?.email)}
          {row(Shield, "Rôle", user?.role)}
        </div>

        <form
          onSubmit={changePassword}
          className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 mb-4"
        >
          <div className="flex items-center gap-2 mb-1">
            <Lock className="w-4 h-4 text-indigo-600" />
            <h2 className="font-bold text-slate-900">Modifier le mot de passe</h2>
          </div>
          <p className="text-sm text-slate-500 mb-5">
            Utilisez au moins 12 caractères. Votre session restera ouverte après la modification.
          </p>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Mot de passe actuel</Label>
              <Input
                id="current-password"
                name="current"
                type={showPasswords ? "text" : "password"}
                autoComplete="current-password"
                value={passwords.current}
                onChange={updatePasswordField}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">Nouveau mot de passe</Label>
              <Input
                id="new-password"
                name="next"
                type={showPasswords ? "text" : "password"}
                autoComplete="new-password"
                minLength={12}
                value={passwords.next}
                onChange={updatePasswordField}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmer le nouveau mot de passe</Label>
              <Input
                id="confirm-password"
                name="confirm"
                type={showPasswords ? "text" : "password"}
                autoComplete="new-password"
                minLength={12}
                value={passwords.confirm}
                onChange={updatePasswordField}
                required
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
            <button
              type="button"
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800"
              onClick={() => setShowPasswords((visible) => !visible)}
              aria-label={showPasswords ? "Masquer les mots de passe" : "Afficher les mots de passe"}
            >
              {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showPasswords ? "Masquer" : "Afficher"}
            </button>
            <Button type="submit" disabled={savingPassword} className="bg-indigo-600 hover:bg-indigo-700">
              {savingPassword && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer le nouveau mot de passe
            </Button>
          </div>
        </form>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <h2 className="font-bold text-slate-900 mb-2">Application</h2>
          {row(Server, "Plateforme", "NovaForge — novaforge.pro")}
          {row(Server, "Modules", "Opportunités · Projets · Clients · Onboarding")}
          <p className="text-xs text-slate-400 mt-4">Pour ajouter un utilisateur, contactez l'administrateur.</p>
        </div>
      </div>
    </CRMLayout>
  );
}
